import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import type { AfterViewInit, OnDestroy } from '@angular/core';
import {
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { isDefined } from '@freelancer/utils';
import { Autolinker } from 'autolinker';
import type { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import type { Tag, TagResult } from './tagging.types';
import { TagType } from './tagging.types';

/**
 * Detect `#` or `@` tags and render those as tags.
 */
@Directive({ selector: '[flTagging]', standalone: true })
export class TaggingDirective implements AfterViewInit, OnDestroy {
  private _element = inject(ElementRef);
  private document = inject<Document>(DOCUMENT);
  private platformId = inject(PLATFORM_ID);

  @Input() flTagging: FormControl<string>;
  @Input() flTaggingTypes: readonly TagType[] = [TagType.AT, TagType.HASH_TAG];
  @Input() flTaggingLimit = 100;
  @Output() flTaggingResult = new EventEmitter<TagResult>();

  private autolinker = new Autolinker({
    urls: false,
    email: false,
    phone: false,
    mention: this.flTaggingTypes.includes(TagType.AT) ? 'twitter' : false,
    hashtag: this.flTaggingTypes.includes(TagType.HASH_TAG)
      ? 'facebook'
      : false,
  });

  private formValueChangesSubscription?: Subscription;
  private tags: readonly Tag[] = [];

  private acceptedNode: HTMLInputElement | HTMLTextAreaElement;

  private debugVisitedTags: readonly string[] = [];
  private debugVisitedCount = 0;

  ngAfterViewInit(): void {
    const acceptedNode = this.getAcceptedNode(this._element.nativeElement);
    if (!acceptedNode) {
      this.logDiagnostic(acceptedNode);
      throw new Error('Unsupported element');
    }
    this.acceptedNode = acceptedNode;
    this.formValueChangesSubscription = this.flTagging.valueChanges
      .pipe(filter(isDefined))
      .subscribe(content => this.collectTags(content));
  }

  private logDiagnostic(
    acceptedNode: HTMLInputElement | HTMLTextAreaElement | undefined,
  ): void {
    try {
      const el = this._element.nativeElement as HTMLElement | null;
      const outerHTML = el?.outerHTML ?? '';
      const payload = {
        foundAcceptedNode: !!acceptedNode,
        acceptedNodeTag: acceptedNode?.tagName ?? null,
        isBrowser: isPlatformBrowser(this.platformId),
        isServer: isPlatformServer(this.platformId),
        platformId: String(this.platformId),
        url: this.document?.location?.href ?? null,
        hostTag: el?.tagName ?? null,
        hostId: el?.id || null,
        hostClass: el?.className || null,
        hostIsConnected: el?.isConnected ?? null,
        hostSameOwnerDoc: el?.ownerDocument === this.document,
        hostCtorName: el?.constructor?.name ?? null,
        hostChildNodeCount: el?.childNodes?.length ?? null,
        hostChildElementCount: el?.children?.length ?? null,
        hostFirstElementChildTag: el?.firstElementChild?.tagName ?? null,
        hostOuterHTMLLen: outerHTML.length,
        hostOuterHTMLHead: outerHTML.slice(0, 400),
        outerHTMLHasTextarea: /<textarea[\s>]/i.test(outerHTML),
        outerHTMLHasInput: /<input[\s>]/i.test(outerHTML),
        hasDocument: !!this.document,
        docHasCreateNodeIterator:
          typeof this.document?.createNodeIterator === 'function',
        docHasNodeFilter: typeof NodeFilter !== 'undefined',
        typeofHTMLInputElement: typeof HTMLInputElement,
        typeofHTMLTextAreaElement: typeof HTMLTextAreaElement,
        iteratorVisitedCount: this.debugVisitedCount,
        iteratorVisitedTagSample: this.debugVisitedTags.join(','),
        taggingTypes: (this.flTaggingTypes ?? []).join(','),
        taggingLimit: this.flTaggingLimit,
        hasControl: !!this.flTagging,
        controlValueType: typeof this.flTagging?.value,
        controlValueLength:
          typeof this.flTagging?.value === 'string'
            ? this.flTagging.value.length
            : null,
        controlDirty: this.flTagging?.dirty ?? null,
        controlTouched: this.flTagging?.touched ?? null,
      };
      // eslint-disable-next-line no-console
      console.error('[flTagging:diagnostic]', JSON.stringify(payload));
    } catch (diagnosticError) {
      // eslint-disable-next-line no-console
      console.error(
        '[flTagging:diagnostic] diagnostic itself failed',
        String(diagnosticError),
      );
    }
  }

  ngOnDestroy(): void {
    if (this.formValueChangesSubscription) {
      this.formValueChangesSubscription.unsubscribe();
    }
  }

  private collectTags(text: string): void {
    const matches = this.autolinker.parse(text);
    this.tags = matches.map(match => ({
      content: match.getMatchedText().substring(1),
      offset: match.getOffset(),
      type: this.getTagType(match.getType()),
    }));

    const caretPosition = this.getCaretPosition();
    if (caretPosition) {
      this.collectEmptyTag(text, caretPosition);
    }

    this.flTaggingResult.emit({
      current: caretPosition ? this.getTagByPosition(caretPosition) : undefined,
      tags: this.tags,
    });
  }

  private getTagType(type: string): TagType {
    switch (type) {
      case 'mention':
        return TagType.AT;

      case 'hashtag':
        return TagType.HASH_TAG;

      default:
        throw new Error(`Unexpected tagging symbol: ${type}`);
    }
  }

  private collectEmptyTag(text: string, caretPosition: number): void {
    let emptyTag: TagType | null = null;
    const tagType = text.substring(caretPosition - 1, caretPosition);
    if (tagType === TagType.AT && this.flTaggingTypes.includes(TagType.AT)) {
      emptyTag = TagType.AT;
    } else if (
      tagType === TagType.HASH_TAG &&
      this.flTaggingTypes.includes(TagType.HASH_TAG)
    ) {
      emptyTag = TagType.HASH_TAG;
    }

    if (emptyTag !== null) {
      this.tags = [
        ...this.tags,
        {
          content: '',
          offset: caretPosition - 1,
          type: emptyTag,
        },
      ];
    }
  }

  private getTagByPosition(offset: number): Tag | undefined {
    const result = this.tags.find(
      tag =>
        tag.offset < offset && tag.offset + tag.content.length + 1 >= offset,
    );
    if (!result) {
      return undefined;
    }
    this.tags = [...this.tags.filter(tag => tag !== result)];
    return result;
  }

  /**
   * Calculates the caret position in the acceptedNode.
   * This position is the offset in the text.
   *
   * @return number|null
   */
  private getCaretPosition(): number | null {
    // for texterea/input element
    return this.acceptedNode.selectionStart;
  }

  private getAcceptedNode(
    element: Node,
  ): HTMLInputElement | HTMLTextAreaElement | undefined {
    const nodeIterator = this.document.createNodeIterator(
      element,
      NodeFilter.SHOW_ELEMENT,
      null,
    );

    const visitedTags: string[] = [];
    let visitedCount = 0;
    let node: HTMLInputElement | HTMLTextAreaElement | undefined;
    while (nodeIterator.nextNode()) {
      const currentNode = nodeIterator.referenceNode;
      visitedCount += 1;
      if (visitedTags.length < 20) {
        visitedTags.push(
          (currentNode as Element)?.tagName ?? typeof currentNode,
        );
      }
      if (this.isAcceptedNode(currentNode)) {
        node = currentNode;
        break;
      }
    }
    this.debugVisitedTags = visitedTags;
    this.debugVisitedCount = visitedCount;

    return node;
  }

  private isAcceptedNode(
    currentNode: Node,
  ): currentNode is HTMLInputElement | HTMLTextAreaElement {
    return (
      currentNode instanceof HTMLInputElement ||
      currentNode instanceof HTMLTextAreaElement
    );
  }
}
